import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { STATUS_LABELS } from '@/lib/adminApi';

const HIDDEN_KEYS = new Set(['_id', 'id', 'activity_log', 'updated_at', 'resend_contact_id']);
const FRIENDLY_LABELS = {
  kind: 'Type',
  organization: 'Organization',
  event_name: 'Event Name',
  event_type: 'Event Type',
  event_date: 'Event Date',
  event_location: 'Location',
  event_format: 'Format',
  audience_size: 'Audience Size',
  budget: 'Budget',
  topic: 'Topic',
  message: 'Message',
  created_at: 'Created',
  updated_at: 'Updated',
  source: 'Source',
  status: 'Status',
  notes: 'Notes',
  first_name: 'First Name',
  last_name: 'Last Name',
  resend_sync_status: 'Resend Sync',
  resend_synced_at: 'Last Synced',
  product: 'Product',
  phone: 'Phone',
  subject: 'Subject',
  email: 'Email',
  name: 'Name',
};

const humanLabel = (k) => FRIENDLY_LABELS[k] || k.replace(/_/g, ' ');

const fmtValue = (k, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (k.endsWith('_at')) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  }
  if (k === 'status') return STATUS_LABELS[v] || v;
  return String(v);
};

export default function DetailDrawer({ section, record, statuses, onClose, onUpdate, onResync }) {
  const [status, setStatus] = useState(record.status || '');
  const [notes, setNotes] = useState(record.notes || '');
  const [saving, setSaving] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  useEffect(() => {
    setStatus(record.status || '');
    setNotes(record.notes || '');
  }, [record.id, record.status, record.notes]);

  const saveStatus = async () => {
    if (status === record.status) return;
    setSaving(true);
    try {
      await onUpdate(record.id, { status });
      toast.success('Status updated.');
    } catch (e) {
      toast.error('Failed to update status.');
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    if ((notes || '') === (record.notes || '')) return;
    setSaving(true);
    try {
      await onUpdate(record.id, { notes });
      toast.success('Notes saved.');
    } catch (e) {
      toast.error('Failed to save notes.');
    } finally {
      setSaving(false);
    }
  };

  const doResync = async () => {
    setResyncing(true);
    try {
      const r = await onResync(record.id);
      if (r?.result?.status === 'synced') toast.success('Resynced with Resend.');
      else toast.error(`Resync failed: ${r?.result?.error || 'unknown error'}`);
    } catch (e) {
      toast.error('Resync failed.');
    } finally {
      setResyncing(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(record.email || '');
      toast.success('Email copied.');
    } catch (_) {
      toast.error('Copy failed.');
    }
  };

  const displayEntries = Object.entries(record).filter(
    ([k]) => !HIDDEN_KEYS.has(k) && k !== 'status' && k !== 'notes' && k !== 'type'
  );

  const activity = Array.isArray(record.activity_log) ? [...record.activity_log].reverse() : [];

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-40 bg-neutral-900/30 backdrop-blur-sm"
        data-testid="detail-drawer-overlay"
      />
      <aside
        className="fixed right-0 top-0 z-50 h-full w-full lg:w-[560px] bg-[#FAF8F5] border-l border-neutral-200/70 overflow-y-auto"
        data-testid="detail-drawer"
      >
        <div className="sticky top-0 z-10 bg-[#FAF8F5]/95 backdrop-blur border-b border-neutral-200/60 px-6 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="uppercase tracking-[0.24em] text-[10px] text-neutral-500 mb-1">{section}</div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl text-neutral-900">
              {record.name || record.email || record.organization || 'Record'}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="detail-drawer-close"
            className="text-neutral-500 hover:text-neutral-900 text-2xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-6 lg:px-8 py-6 space-y-8">
          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {record.email && (
              <>
                <button
                  data-testid="detail-copy-email"
                  onClick={copyEmail}
                  className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-neutral-300 rounded-full hover:bg-neutral-100"
                >
                  Copy Email
                </button>
                <a
                  data-testid="detail-mailto"
                  href={`mailto:${record.email}`}
                  className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-neutral-300 rounded-full hover:bg-neutral-100"
                >
                  Email
                </a>
              </>
            )}
            {onResync && (
              <button
                data-testid="detail-resync"
                onClick={doResync}
                disabled={resyncing}
                className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-neutral-300 rounded-full hover:bg-neutral-100 disabled:opacity-60"
              >
                {resyncing ? 'Resyncing…' : 'Resync to Resend'}
              </button>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">Status</label>
            <div className="flex gap-2">
              <select
                data-testid="detail-status-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="flex-1 bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900"
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                ))}
              </select>
              <button
                data-testid="detail-status-save"
                onClick={saveStatus}
                disabled={saving || status === record.status}
                className="bg-neutral-900 text-white uppercase tracking-[0.2em] text-[10px] px-5 rounded-lg hover:bg-[#1a1a1a] disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">Internal Notes</label>
            <textarea
              data-testid="detail-notes-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900"
              placeholder="Private notes only visible in Founder Command…"
            />
            <div className="mt-2 flex justify-end">
              <button
                data-testid="detail-notes-save"
                onClick={saveNotes}
                disabled={saving || (notes || '') === (record.notes || '')}
                className="bg-neutral-900 text-white uppercase tracking-[0.2em] text-[10px] px-5 py-2 rounded-full hover:bg-[#1a1a1a] disabled:opacity-50"
              >
                Save Notes
              </button>
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">Details</div>
            <dl className="divide-y divide-neutral-200/60 border border-neutral-200/60 rounded-lg bg-white/60">
              {displayEntries.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 px-4 py-3">
                  <dt className="col-span-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500 pt-0.5">{humanLabel(k)}</dt>
                  <dd className="col-span-2 text-[13px] text-neutral-800 whitespace-pre-wrap break-words">{fmtValue(k, v)}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Activity Log */}
          <div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">Activity</div>
            {activity.length === 0 ? (
              <div className="text-[12px] text-neutral-500">No activity yet.</div>
            ) : (
              <ul className="space-y-3">
                {activity.map((a, i) => (
                  <li key={i} className="text-[12px] text-neutral-700">
                    <div className="text-neutral-500 text-[10px] uppercase tracking-[0.18em]">
                      {a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}
                    </div>
                    <div className="mt-0.5">
                      {a.action.replace(/_/g, ' ')}{a.detail ? ` — ${a.detail}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
